/**
 * The rules behind editing ONE CONTACT'S PLAN — the `enrollment_steps` rows.
 *
 * Named for the concern rather than the component, like enrollment.ts beside
 * LifecyclePanel.tsx and propagation.ts beside the dialog it has no screen
 * for yet. The screen here is LifecycleScreen.tsx.
 *
 * WHY THE RULES ARE HERE AND NOT IN THE COMPONENT
 * -----------------------------------------------
 * The `marketing` vitest project globs plain `.ts` and runs in Node at ~1s;
 * `app` runs in real Chromium. Everything below decides either what is
 * WRITTEN to `enrollment_steps` or whether a person is told the truth about
 * why a control is unavailable, and none of it needs a DOM.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE FOUR FACTS THIS FILE EXISTS TO KEEP ON SCREEN
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 1. ONLY A `pending` STEP MAY BE REMOVED, AND THE DATABASE MEANS IT.
 *    `enrollment_steps_only_pending_is_removable` is a BEFORE DELETE trigger
 *    (072), not a CHECK — a CHECK constrains the values a row may hold and is
 *    never consulted on a DELETE, which is why 071 shipped this rule as a
 *    comment and a step inserted as `generated` deleted perfectly happily.
 *    The trigger exempts the ON DELETE CASCADE from `enrollments`, so a
 *    contact whose plan produced a task can still be deleted.
 *
 *    Every refusal below is a SENTENCE, and the screen renders it beside a
 *    disabled control rather than hiding the control. A control that vanishes
 *    teaches nothing; a disabled one with no explanation is its own kind of
 *    lie.
 *
 * 2. THE STEP AND ITS TASK ARE JOINED AT BIRTH AND INDEPENDENT AFTER.
 *    `tasks.enrollment_step_id` is the only link and it points one way.
 *    Ticking the task does NOT move the step out of `generated`; nothing on
 *    the step writes to the task. So stepTaskState() reports the step's real
 *    status and the task's real status SIDE BY SIDE, and when they disagree
 *    it says so rather than picking a winner. That disagreement is the truth
 *    of this schema, and a screen that smoothed it over would be inventing a
 *    synchronization nothing performs.
 *
 * 3. A `generated` STEP MUST NOT BE OFFERED `skipped`.
 *
 *    ══ THE SCHEMA GAP THIS USED TO RECORD IS CLOSED (092-era, migration 091) ══
 *    This block used to say the database would accept
 *        update enrollment_steps set status = 'skipped', skipped_reason = '…'
 *         where status = 'generated'
 *    and that skipVerdict() was therefore the only guard. That was true, and
 *    is no longer: `enrollment_steps_generated_is_not_skippable` (091) now
 *    refuses it, raising the SAME SENTENCE this function returns — verbatim,
 *    and the suite asserts the wording, so the two cannot drift into telling
 *    a person two different stories about the same refusal.
 *
 *    So this function is no longer load-bearing for correctness, and it is
 *    still worth having: it refuses in the UI, before a round trip, with a
 *    reason attached. The database refuses whatever bypasses the UI — psql,
 *    the data provider, a future screen. Two layers saying one thing.
 *
 *    ONE PATH SURVIVES, DELIBERATELY: `generated -> pending -> skipped` in two
 *    statements is still permitted, because `generated -> pending` is how this
 *    plan's own reorder and teardown paths work. Closing it would freeze
 *    `generated` outright and break them. It is two deliberate statements, not
 *    a slip, which is a different act from the one the guard exists to stop.
 *    ═══════════════════════════════════════════════════════════════════════
 *
 * 4. ADDING A STEP MID-PLAN MOVES EVERY PENDING STEP AFTER IT.
 *    `offset_days` counts from the PREVIOUS step (052), so a step inserted
 *    with a three-day wait pushes the whole tail out by three days — and
 *    every later step's own `offset_days` stays correct precisely because
 *    they all move together. planStepInsertion() computes that shift as data
 *    so the screen can show it BEFORE anything is written. Shifting only the
 *    step immediately after would bunch the rest of the plan against it, the
 *    same failure shiftAfterPause() exists to prevent on resume.
 * ══════════════════════════════════════════════════════════════════════════
 */

import type { DataProvider } from "ra-core";

import type { EnrollmentStep } from "./types";

/* ── what may be done to one step ──────────────────────────────────────── */

/**
 * Yes, or no with the reason.
 *
 * A DISCRIMINATED UNION rather than `{ ok: boolean; reason?: string }`,
 * following ReasonCheck in outboundRules.ts and PropagationVerdict in
 * propagation.ts: the optional-field version typechecks perfectly with the
 * reason left off, which is the one mistake these verdicts exist to prevent.
 */
export type StepVerdict = { ok: true } | { ok: false; reason: string };

/** The step fields every verdict below is decided from, and no others. */
export type StepFacts = Pick<EnrollmentStep, "status"> &
  Partial<Pick<EnrollmentStep, "skipped_reason">>;

/**
 * An unrecognized status is a REFUSAL that says the word back.
 *
 * `status` is a plain text column with a default and the database is free to
 * grow a fourth value without this file being told — the posture
 * canPropagate() and describeWait() both take. Falling through to "allowed"
 * would let an unknown state be quietly rewritten; falling through to a
 * generic "no" would hide that a state exists nobody here has heard of.
 */
const unknownStatusText = (status: string, verb: string): string =>
  `This step's status is “${status}”, which this screen has never been told about. It will not ${verb} something it cannot describe — check the row in the database rather than trusting anything here about it.`;

const unknownStatus = (status: string, verb: string): StepVerdict => ({
  ok: false,
  reason: unknownStatusText(status, verb),
});

/**
 * May this step be removed from the plan?
 *
 * THE SENTENCE FOR `generated` IS THE TRIGGER'S OWN ARGUMENT, in fewer words.
 * The row produced a task, `tasks.enrollment_step_id` is ON DELETE SET NULL
 * so that task SURVIVES the delete, and what is left is a task pointing at
 * nothing — the record of work having been asked for, detached from the
 * reason it was asked for.
 */
export function removalVerdict(step: StepFacts): StepVerdict {
  switch (step.status) {
    case "pending":
      return { ok: true };
    case "generated":
      return {
        ok: false,
        reason:
          "This step has already produced a task, so the database refuses to delete it — the task survives a removed step, and a task pointing at nothing is a record of work asked for with no record of why. Nothing here can override that trigger.",
      };
    case "skipped":
      return {
        ok: false,
        reason:
          "This step was skipped, and the reason somebody wrote is the only trace that decision left. Deleting it would erase the record of a deliberate choice, so the database refuses that too.",
      };
    default:
      return unknownStatus(step.status, "remove");
  }
}

/**
 * May this step be skipped?
 *
 * A `generated` STEP IS REFUSED HERE AND NOWHERE ELSE — see fact 3 in the
 * header. The database would accept the update today.
 */
export function skipVerdict(step: StepFacts): StepVerdict {
  switch (step.status) {
    case "pending":
      return { ok: true };
    case "generated":
      return {
        ok: false,
        reason:
          "This step has already run and written a task, and somebody may have acted on it. Skipping it now would put on the record that we deliberately did not do a thing we did. Close the task instead if it is no longer wanted.",
      };
    case "skipped":
      return {
        ok: false,
        reason: step.skipped_reason
          ? `Already skipped — “${step.skipped_reason}”.`
          : "Already skipped.",
      };
    default:
      return unknownStatus(step.status, "skip");
  }
}

/**
 * May this step's date be moved?
 *
 * `generated` AND `skipped` ARE HISTORY AND DO NOT MOVE — the same rule
 * shiftAfterPause() applies on resume, and for the same two reasons: moving a
 * generated step's date makes the step disagree with the task it produced,
 * and redating a skipped step makes somebody's note describe a day nobody
 * wrote it on.
 */
export function retimeVerdict(step: StepFacts): StepVerdict {
  switch (step.status) {
    case "pending":
      return { ok: true };
    case "generated":
      return {
        ok: false,
        reason:
          "This step has already produced a task with a due date of its own. Moving the step's date now would make the two disagree about the same piece of work — change the task's due date instead.",
      };
    case "skipped":
      return {
        ok: false,
        reason:
          "This step was skipped on a particular day, and the reason recorded belongs to that day. Redating it would make that note describe a day nobody wrote it on.",
      };
    default:
      return unknownStatus(step.status, "retime");
  }
}

/* ── one lock, or three refusals ────────────────────────────────── */

/**
 * Whether this step's plan can be changed at all, for the SCREEN to print.
 *
 * WHY THIS IS NOT JUST THE THREE VERDICTS ABOVE. All three decide on exactly
 * one discriminant -- `step.status` -- and every one of them is `ok` when and
 * only when it is `pending`. So the screen was printing three refusals that
 * could never appear apart: three restatements of a single fact, which a
 * reader had to compare word by word to discover always agreed.
 *
 * BUT IT DOES NOT ASSUME THEY AGREE. It asks all three and merges only when
 * all three refuse. Give a future step a rule that lets it be retimed but not
 * removed and `scope` comes back `"some"`, the screen prints the individual
 * reasons again, and nobody has to remember to undo a consolidation -- the
 * divergence restores the itemized display by itself. planRules.test.ts
 * asserts both branches, so a rule that diverges cannot do it silently.
 *
 * THE THREE VERDICTS REMAIN THE WRITE GUARDS and keep their own wording.
 * A refusal at write time should say why THAT write was refused, not
 * summarize two others nobody attempted.
 */
export type PlanEditLock =
  | { locked: false }
  | { locked: true; scope: "all"; reason: string }
  | {
      locked: true;
      scope: "some";
      refusals: readonly { action: string; reason: string }[];
    };

/**
 * The one sentence for a step nothing may be done to.
 *
 * Each case states the FACT first and the consequences after, because the
 * fact is what a reader can check and the consequences are what they would
 * otherwise have to infer from three separate paragraphs.
 */
const lockedSentence = (step: StepFacts): string => {
  switch (step.status) {
    case "generated":
      return "This step has already produced a task, so its plan is now fixed: removing it would leave that task pointing at nothing, skipping it would record that we deliberately did not do a thing we did, and moving its date would make the step and the task disagree about the same work. Change the task instead — nothing here writes back to it.";
    case "skipped":
      return "This step was skipped, and the reason somebody wrote is the only trace that decision left. Removing it would erase a deliberate choice, redating it would make that note describe a day nobody wrote it on, and it cannot be skipped a second time.";
    default:
      return unknownStatusText(step.status, "change");
  }
};

/**
 * The three write guards, in the order the buttons sit in.
 *
 * INJECTABLE ONLY SO THE DIVERGENCE BRANCH CAN BE TESTED. There is no way to
 * reach `scope: "some"` with the real three -- that is the whole point of
 * them -- so without this the branch that protects the consolidation would
 * be the one piece of it nothing ever ran.
 */
export const PLAN_CHECKS: readonly (readonly [
  string,
  (step: StepFacts) => StepVerdict,
])[] = [
  ["retime", retimeVerdict],
  ["skip", skipVerdict],
  ["remove", removalVerdict],
];

export function planEditLock(
  step: StepFacts,
  checks: readonly (readonly [
    string,
    (step: StepFacts) => StepVerdict,
  ])[] = PLAN_CHECKS,
): PlanEditLock {
  const refusals: { action: string; reason: string }[] = [];
  for (const [action, check] of checks) {
    const verdict = check(step);
    if (!verdict.ok) refusals.push({ action, reason: verdict.reason });
  }

  if (refusals.length === 0) return { locked: false };
  if (refusals.length < checks.length)
    return { locked: true, scope: "some", refusals };
  return { locked: true, scope: "all", reason: lockedSentence(step) };
}

export type SkipReasonCheck =
  | { ok: true; reason: string }
  | { ok: false; message: string };

/**
 * Can this be the reason a step was skipped?
 *
 * REFUSED HERE AND BY THE DATABASE. `enrollment_steps_skip_has_reason` is
 * CHECK (status <> 'skipped' or skipped_reason is not null), so an
 * unaccountable skip cannot be stored — but a constraint error in a toast
 * teaches nothing, and the point is not to pass the check. The same argument
 * validateEndReason() makes about an ending: six months later the plan says a
 * step did not happen and nothing says why, so nobody can tell a considered
 * decision from a mis-click.
 *
 * There is deliberately no optional path. An empty string would satisfy the
 * constraint while defeating it — and `skipped_reason` is NULLABLE, so the
 * empty string is a value the column would happily take.
 */
export function validateSkipReason(
  raw: string | null | undefined,
): SkipReasonCheck {
  const reason = (raw ?? "").trim();
  if (!reason)
    return {
      ok: false,
      message:
        "Say why this step is being skipped. A step that disappears from the plan with no reason is indistinguishable from one nobody meant to drop.",
    };
  return { ok: true, reason };
}

/* ── moving a date without moving a clock ──────────────────────────────── */

/**
 * A chosen calendar day, applied to a step's existing `due_at`.
 *
 * THE TIME OF DAY IS KEPT, AND THAT IS THE WHOLE FUNCTION. `new
 * Date("2026-09-07")` is UTC midnight — the 6th at 8pm in New York — so
 * writing the date input straight through would move every step to the
 * evening BEFORE the day somebody picked, for anyone west of Greenwich. The
 * generator's test is `due_at <= now()` against a job that runs once a day,
 * so that is not cosmetic: it is a step firing a day early.
 *
 * The date parts are read as LOCAL, matching dateInputValue() in touchEdit.ts
 * which is what seeds the input in the first place. A round trip through the
 * two must be the identity, or opening a dialog and pressing save moves a
 * date nobody touched.
 */
export function retimedDueAt(currentDueAt: string, day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  const next = new Date(currentDueAt);
  next.setFullYear(y, (m || 1) - 1, d || 1);
  return next.toISOString();
}

/** A whole number of days added to a timestamp, as an ISO string. */
const MS_PER_DAY = 86_400_000;
export const plusDays = (iso: string, days: number): string =>
  new Date(new Date(iso).getTime() + days * MS_PER_DAY).toISOString();

export type DueDayCheck =
  | { ok: true; day: string }
  | { ok: false; message: string };

/**
 * Is this a date a step can be moved to?
 *
 * A DATE IN THE PAST IS ALLOWED, DELIBERATELY, and this is the one refusal
 * that is NOT here. `due_at <= now()` is the generator's test, so a step
 * dated yesterday simply fires on the next run — which is exactly what
 * somebody means when they drag a step forward to "send this now". Refusing
 * it would remove the most obvious reason to retime anything.
 *
 * What IS refused is an empty or unparseable box, because the date input
 * reports both as "" and writing that to a NOT NULL timestamptz is a
 * constraint error on a screen that has done nothing wrong.
 */
export function validateDueDay(raw: string | null | undefined): DueDayCheck {
  const day = (raw ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day))
    return {
      ok: false,
      message: "Pick a date for this step. `due_at` cannot be empty.",
    };
  const [y, m, d] = day.split("-").map(Number);
  const probe = new Date(y, (m || 1) - 1, d || 1);
  if (
    probe.getFullYear() !== y ||
    probe.getMonth() !== (m || 1) - 1 ||
    probe.getDate() !== d
  )
    return { ok: false, message: `${day} is not a real date.` };
  return { ok: true, day };
}

/* ── the step and the task it produced ─────────────────────────────────── */

/** The task columns this screen reads. `tasks` is upstream's table. */
export type StepTask = {
  id: number;
  enrollment_step_id: number | null;
  type: string | null;
  text: string | null;
  due_date: string | null;
  done_date: string | null;
};

export type StepTaskState = {
  /**
   * `agree` is not "all is well" — it is "these two say compatible things".
   * `disagree` is the state this whole type exists to make visible.
   */
  kind: "none" | "waiting" | "open" | "done" | "disagree";
  sentence: string;
};

/**
 * What the step says, what the task says, and whether they agree.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE STEP AND THE TASK ARE INDEPENDENT AFTER BIRTH, AND THE SCREEN SAYS SO.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `tasks.enrollment_step_id` is the ONLY link between them and it points one
 * way. Nothing writes back: closing the task leaves the step `generated` for
 * ever, and nothing on the step touches the task. Two states, two writers,
 * no synchronization.
 *
 * So this function never derives one from the other. A `generated` step whose
 * task is done is reported as "the step ran; the task is done" — two facts,
 * both true. And the two cases where they genuinely contradict each other get
 * `disagree` and a sentence naming the contradiction:
 *
 *   - a `generated` step with NO task row. Either the task was deleted (an
 *     ordinary DELETE policy on `tasks` allows it) or the write half-landed.
 *     The step will never be regenerated: 078 only ever moves a step from
 *     pending, so this work is now invisible to everything except this line.
 *   - a `pending` or `skipped` step that HAS a task. The plan says this has
 *     not happened, and a task on somebody's list says it was asked for.
 *
 * Neither is hypothetical enough to leave out: `tasks` has an unconditional
 * DELETE policy for every authenticated user, and a step is retimeable and
 * skippable from this very screen while its task sits untouched.
 */
export function stepTaskState(
  step: Pick<EnrollmentStep, "status">,
  task: StepTask | null | undefined,
): StepTaskState {
  const done = task?.done_date != null;

  if (step.status === "generated") {
    if (!task)
      return {
        kind: "disagree",
        sentence:
          "This step ran, but the task it wrote is gone. Nothing will re-create it — the nightly run only ever moves a step out of pending — so this piece of work now exists only as this row.",
      };
    return done
      ? {
          kind: "done",
          sentence:
            "The step ran and its task is closed. Closing the task did not move the step; the two are recorded separately and always will be.",
        }
      : {
          kind: "open",
          sentence:
            "The step ran and its task is still open. The step stays `generated` whatever happens to the task — nothing writes back.",
        };
  }

  if (task)
    return {
      kind: "disagree",
      sentence: done
        ? "This step has not run, yet a closed task points at it. The plan and the task list disagree about whether this happened."
        : "This step has not run, yet an open task points at it. The plan and the task list disagree about whether this was asked for.",
    };

  if (step.status === "skipped")
    return {
      kind: "none",
      sentence: "Skipped before it ran, so no task was ever written.",
    };

  return {
    kind: "waiting",
    sentence: "No task yet. The nightly run writes one when the date arrives.",
  };
}

/* ── when anything will actually happen ────────────────────────────────── */

/** One row of `public.pipeline_runs`, reduced to what this screen reads. */
export type NurtureRun = {
  job: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  detail: string | null;
};

/** The job whose absence made this feature look broken for 21 hours. */
export const NURTURE_JOB = "nurture-tick";

export type NurtureRunState = {
  /** The newest `nurture-tick` row, or null if there has never been one. */
  lastRunAt: string | null;
  /** Whether that run reported `ok`. */
  healthy: boolean;
  /** Whole hours since it started. Null when there is no run to measure. */
  hoursSince: number | null;
  /**
   * True once it is far enough past a daily cadence that the job has probably
   * not run rather than merely not run yet.
   */
  overdue: boolean;
  /** The whole thing, in a sentence, for the screen. */
  sentence: string;
};

/**
 * How long a daily job may be silent before silence is the story.
 *
 * 36 HOURS, NOT 24. `.github/workflows/nurture.yml` says in as many words
 * that GitHub does not start scheduled workflows when the cron says — the
 * cron is set four hours early because runs were measured landing about four
 * hours late, and that compensation is "an observation, not a guarantee". A
 * 24-hour threshold would therefore cry wolf on an ordinary day. 36 leaves
 * room for the drift and still catches a job that has genuinely stopped,
 * which is the case worth a red sentence.
 */
export const NURTURE_STALE_HOURS = 36;

/**
 * What the nightly job last did, and what that means for this plan.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THIS SENTENCE IS WHY THIS SCREEN EXISTS.
 * ══════════════════════════════════════════════════════════════════════════
 * A contact was enrolled, the plan was written correctly, five steps were
 * dated, and the person who did it concluded the feature was broken — because
 * nothing anywhere said that a step becomes a task only when a job runs, once
 * a day, elsewhere. Twenty-one hours of believing a working system was dead.
 *
 * The rows are NOT filtered here. Filtering by `job` is the caller's query;
 * this function takes whatever it is given and picks the newest nurture-tick
 * row, so a caller that forgets the filter still gets an answer about the
 * right job rather than about drain-outbound.
 *
 * NO NEXT-RUN TIME IS PREDICTED, and that is deliberate. The workflow's own
 * comments say the schedule is not honored — "it will drift, and it can
 * disappear entirely" — so a screen printing "next run 06:00" would be
 * stating a time the system does not promise. What CAN be said honestly is
 * when it last ran and how long ago that was, which is the fact somebody
 * needs in order to know whether to wait or to go and look.
 */
export function describeNurtureRun(
  runs: readonly NurtureRun[] | null | undefined,
  now: Date = new Date(),
): NurtureRunState {
  const last = (runs ?? [])
    .filter((r) => r.job === NURTURE_JOB)
    .sort((a, b) => (a.started_at < b.started_at ? 1 : -1))[0];

  if (!last)
    return {
      lastRunAt: null,
      healthy: false,
      hoursSince: null,
      overdue: true,
      sentence:
        "The nightly job that turns a due step into a task has no recorded run at all. Nothing on this plan will happen until it does — a step becomes a task only when that job runs.",
    };

  const hoursSince = Math.floor(
    (now.getTime() - new Date(last.started_at).getTime()) / 3_600_000,
  );
  const healthy = last.status === "ok";
  const overdue = hoursSince >= NURTURE_STALE_HOURS;

  const when =
    hoursSince <= 0
      ? "less than an hour ago"
      : hoursSince === 1
        ? "an hour ago"
        : hoursSince < 48
          ? `${hoursSince} hours ago`
          : `${Math.floor(hoursSince / 24)} days ago`;

  if (!healthy)
    return {
      lastRunAt: last.started_at,
      healthy,
      hoursSince,
      overdue,
      sentence: `Nothing happens until the nightly job runs, and its last run — ${when} — reported “${last.status}”${last.detail ? ` (${last.detail})` : ""}. Until it succeeds, no due step becomes a task.`,
    };

  if (overdue)
    return {
      lastRunAt: last.started_at,
      healthy,
      hoursSince,
      overdue,
      sentence: `Nothing happens until the nightly job runs, and it has not run for ${when.replace(" ago", "")}. It is meant to run daily, so a due step is sitting here uncollected.`,
    };

  return {
    lastRunAt: last.started_at,
    healthy,
    hoursSince,
    overdue,
    sentence: `Nothing happens until the nightly job runs — it last ran ${when}${last.detail ? ` (${last.detail})` : ""}. That job is what turns a due step into a task, once a day.`,
  };
}

/* ── editing the plan ──────────────────────────────────────────────────── */

/** One write, as the data provider takes it. Order within a plan matters. */
export type StepWrite = {
  id: number;
  data: Partial<
    Pick<EnrollmentStep, "step_no" | "due_at" | "offset_days" | "edited">
  >;
};

/** Ascending by step_no. Never mutates the caller's array. */
const inOrder = <T extends { step_no: number }>(steps: readonly T[]) =>
  [...steps].sort((a, b) => a.step_no - b.step_no);

/**
 * WHY EVERY WRITE BELOW SETS `edited`.
 *
 * `edited` is propagation's second condition — canPropagate() refuses a step
 * somebody has changed by hand, because "the sequence no longer speaks for
 * it". A step MOVED by an insertion was not typed into by a person, but it
 * was moved by a person's decision about this contact, and a later template
 * edit silently pulling it back to the template's date would undo that
 * decision without saying so. So a moved step is an edited step.
 */
const EDITED = { edited: true } as const;

export type RemovalPlan = {
  remove: EnrollmentStep;
  /**
   * The step that inherits the removed step's wait, or null when the last
   * step is removed. Its `offset_days` grows so that NO DATE MOVES.
   */
  absorbedBy: EnrollmentStep | null;
  absorbedDays: number;
  /**
   * The updates, IN THE ORDER THEY MUST BE WRITTEN — see the note below.
   * The delete is the caller's first action and is not in this list.
   */
  writes: StepWrite[];
  /** The plan as it will be once every write lands. What the UI previews. */
  after: EnrollmentStep[];
};

/**
 * Everything that has to happen to remove one step, worked out first.
 *
 * NO DATE MOVES, AND THAT IS THE DESIGN DECISION. `offset_days` counts from
 * the previous step, so dropping a step whose wait was three days would pull
 * every later step three days earlier if the offsets were left alone —
 * nobody asked for that and nothing would report it. planRemoval() in
 * sequenceRules.ts makes exactly this argument about a template; here the
 * dates are real and stored, so the fix is the same in reverse: the removed
 * wait is added to the successor's `offset_days`, every stored `due_at` stays
 * where it is, and the two remain consistent with each other.
 *
 * THE RENUMBER ORDER IS PART OF THE CONTRACT. `UNIQUE (enrollment_id,
 * step_no)` is DEFERRABLE INITIALLY IMMEDIATE, so it is checked per statement
 * — and these are separate PostgREST requests with no transaction around
 * them. Every renumbered row moves DOWN by one, so writing them ASCENDING
 * means each write targets a number the row below it has already vacated.
 * Descending would try to give step 3 the number 2 while step 2 still holds
 * it, and fail halfway through.
 *
 * Returns null when the id is not in the list, so a stale click after
 * somebody else removed the same step is a no-op rather than a delete of
 * whatever now sits in that position.
 */
export function planStepRemoval(
  steps: readonly EnrollmentStep[],
  stepId: number,
): RemovalPlan | null {
  const ordered = inOrder(steps);
  const index = ordered.findIndex((s) => s.id === stepId);
  if (index === -1) return null;

  const remove = ordered[index];
  const rest = ordered.filter((_, i) => i !== index);
  const absorbedBy = index < ordered.length - 1 ? ordered[index + 1] : null;

  /*
   * ══════════════════════════════════════════════════════════════════════
   * THE TAIL MOVES DOWN BY ONE FROM ITS OWN NUMBER. IT IS NOT RENUMBERED
   * 1..n.
   * ══════════════════════════════════════════════════════════════════════
   * `step_no` on THIS table is order, not position, and it is NOT required
   * to be contiguous -- 078 walks these rows by `status` and `due_at` and
   * does no arithmetic on the number at all, so 1, 3, 7 is a legal plan.
   * (`sequence_steps` is the opposite, which is why planRemoval() over there
   * renumbers to 1..n; copying that rule here was a bug.)
   *
   * Renumbering blindly is not merely cosmetic on a plan that already has a
   * hole. Take 2, 3, 4 and remove 3: positions 1..n give the survivors 1 and
   * 2, but only the rows AFTER the removed one are written -- so the row
   * still holding 2 is never told, and the write of 4 -> 2 lands on a number
   * that is still occupied. A unique violation, halfway through, with no
   * transaction to undo it.
   *
   * Decrementing instead cannot collide: every tail row starts ABOVE the
   * removed number, so the smallest of them lands no lower than the removed
   * number itself, which the delete has already freed -- and the rows below
   * it are lower still. Writing them ascending keeps that true all the way up.
   */
  const after = rest.map((step) => ({
    ...step,
    step_no: step.step_no > remove.step_no ? step.step_no - 1 : step.step_no,
    offset_days:
      step.id === absorbedBy?.id
        ? step.offset_days + remove.offset_days
        : step.offset_days,
  }));

  return {
    remove,
    absorbedBy,
    absorbedDays: absorbedBy ? remove.offset_days : 0,
    // Only the rows that actually move. Steps BEFORE the removed one keep
    // their number and their wait, and rewriting them would be a request that
    // can fail for no gain.
    writes: after.slice(index).map((s) => ({
      id: s.id,
      data: { step_no: s.step_no, offset_days: s.offset_days, ...EDITED },
    })),
    after,
  };
}

/** What an insertion needs to know about the step being added. */
export type NewStep = {
  /** The step it follows, by id. Null puts it at the front of the plan. */
  afterId: number | null;
  /** Days after that step. `>= 0` — enrollment_steps_offset_check. */
  waitDays: number;
  content_id: number | null;
  task_type: string | null;
};

export type InsertionPlan = {
  /** The row to create, ready for dataProvider.create. */
  create: {
    enrollment_id: number;
    step_no: number;
    offset_days: number;
    due_at: string;
    content_id: number | null;
    task_type: string | null;
    source: "added";
  };
  /**
   * Existing rows to move, IN THE ORDER THEY MUST BE WRITTEN, and the create
   * comes AFTER all of them — see the note on planStepInsertion().
   */
  writes: StepWrite[];
  /** Every pending step whose date changes, with both dates, for the preview. */
  moved: { step: EnrollmentStep; from: string; to: string }[];
  /**
   * Steps after the insertion point that do NOT move, with the reason. Listed
   * rather than omitted: silence about an exclusion is the same defect as
   * silence about a lead.
   */
  frozen: { step: EnrollmentStep; reason: string }[];
  after: EnrollmentStep[];
};

/**
 * Everything that has to happen to add one step, worked out first.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * EVERY PENDING STEP AFTER THE INSERTION POINT MOVES BY THE NEW STEP'S WAIT.
 * ══════════════════════════════════════════════════════════════════════════
 * `offset_days` counts from the previous step, so a step inserted with a
 * three-day wait means everything after it happens three days later than it
 * would have. Moving them ALL by the same delta is what preserves the gaps
 * the plan already described — and it is also what keeps every later step's
 * stored `offset_days` correct without rewriting a single one of them, since
 * each still sits exactly that many days after whatever now precedes it.
 *
 * Moving only the immediately following step would bunch the rest of the plan
 * against it, which is the failure shiftAfterPause() argues about at length.
 *
 * `generated` AND `skipped` STEPS DO NOT MOVE. They are history: a generated
 * step's date is shared with a task that already exists, and a skipped step's
 * date belongs to the day somebody wrote the reason. They are returned in
 * `frozen` with that reason so the preview can list them rather than leave
 * them out.
 *
 * THE WRITE ORDER, AND WHY THE CREATE IS LAST. Every later step's `step_no`
 * moves UP by one, so the numbers must be written DESCENDING — highest first,
 * each into a number nothing holds yet. The new row's own number is only free
 * once all of them have moved, so it is created afterwards. That is the
 * mirror image of planStepRemoval's ascending order, and the same unique
 * index is the reason for both.
 */
export function planStepInsertion(
  steps: readonly EnrollmentStep[],
  enrollmentId: number,
  spec: NewStep,
  now: Date = new Date(),
): InsertionPlan | null {
  const ordered = inOrder(steps);
  const index =
    spec.afterId == null ? -1 : ordered.findIndex((s) => s.id === spec.afterId);
  // A named predecessor that is not in the list is a stale click, not a
  // request to insert at the front — those are different intentions and only
  // one of them was expressed.
  if (spec.afterId != null && index === -1) return null;

  const predecessor = index >= 0 ? ordered[index] : null;
  const dueAt = plusDays(
    predecessor ? predecessor.due_at : now.toISOString(),
    spec.waitDays,
  );

  const tail = ordered.slice(index + 1);
  const moved: InsertionPlan["moved"] = [];
  const frozen: InsertionPlan["frozen"] = [];

  const shifted = tail.map((step) => {
    if (step.status === "pending") {
      const to = plusDays(step.due_at, spec.waitDays);
      if (spec.waitDays !== 0) moved.push({ step, from: step.due_at, to });
      return { ...step, due_at: to };
    }
    frozen.push({
      step,
      reason:
        step.status === "generated"
          ? "already ran and wrote a task, so its date is shared with something that exists"
          : "was skipped on a particular day, and the reason recorded belongs to that day",
    });
    return step;
  });

  /*
   * The new row takes the number just above its predecessor, and every row
   * after it moves up by one from ITS OWN number -- not a renumbering to
   * 1..n. See the long note in planStepRemoval() for why: on a plan that
   * already has a hole, 1..n would hand the new row a number a later row
   * still holds and the insert would fail on the unique index.
   *
   * On a contiguous plan -- which is what enroll_contact() creates and what
   * these two functions preserve -- the two agree exactly.
   */
  const stepNo = (predecessor?.step_no ?? 0) + 1;

  const create = {
    enrollment_id: enrollmentId,
    step_no: stepNo,
    offset_days: spec.waitDays,
    due_at: dueAt,
    content_id: spec.content_id,
    task_type: spec.task_type,
    source: "added" as const,
  };

  const after: EnrollmentStep[] = [
    ...ordered.slice(0, index + 1),
    // A stand-in for the row the database will mint. `id: 0` is never a real
    // identity here — `bigint generated always as identity` starts at 1 — so
    // a preview row can never be mistaken for a saved one.
    {
      ...create,
      id: 0,
      status: "pending" as const,
      skipped_reason: null,
      edited: false,
      // A new step carries no override: it starts as whatever its asset says.
      subject: null,
      body: null,
    },
    ...shifted.map((step) => ({ ...step, step_no: step.step_no + 1 })),
  ];

  return {
    create,
    // DESCENDING. Highest step_no first, so each write lands on a number the
    // row above it has already vacated.
    writes: after
      .slice(index + 2)
      .reverse()
      .map((s) => ({
        id: s.id,
        data: { step_no: s.step_no, due_at: s.due_at, ...EDITED },
      })),
    moved,
    frozen,
    after,
  };
}

export type SwapPlan = {
  /** The two rows changing places, in their current order. */
  first: EnrollmentStep;
  second: EnrollmentStep;
  /** The three writes, IN ORDER. See the note on planStepSwap(). */
  writes: StepWrite[];
  after: EnrollmentStep[];
};

/**
 * Swap a step with its neighbor.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE DATES CHANGE PLACES TOO, AND THAT IS THE POINT.
 * ══════════════════════════════════════════════════════════════════════════
 * 078 walks steps by `status` and `due_at` and does no arithmetic on
 * `step_no` at all. So swapping only the numbers would reorder the LIST and
 * change nothing about what the contact receives or when — a control that
 * appears to work and does nothing, which is worse than no control.
 *
 * What a person means by "move this one earlier" is that this asset should go
 * out on the earlier date. So the SCHEDULE stays attached to the position and
 * the STEP moves through it: each row takes the other's `step_no`, `due_at`
 * and `offset_days`. Every date in the plan is exactly where it was; only
 * which asset lands on which date has changed — which also leaves both
 * `offset_days` values still describing the gaps they always described.
 *
 * BOTH STEPS MUST BE PENDING, and retimeVerdict() is the check, because a
 * swap IS a retime of both. A generated step's date is shared with a task
 * that already exists; a skipped step's date belongs to the day its reason
 * was written.
 *
 * THREE WRITES, AND A NUMBER NOTHING ELSE HOLDS IN THE MIDDLE.
 * `enrollment_steps_enrollment_id_step_no_key` is DEFERRABLE INITIALLY
 * IMMEDIATE, so a single UPDATE that swaps both rows would work inside a
 * transaction after `set constraints … deferred`. PostgREST gives us neither
 * — one statement per request, no transaction spanning them — so the swap
 * goes through a parking number instead: max + 1, which the unique index
 * guarantees is free. An interruption between the writes leaves one step
 * parked at a visible, out-of-range position with nothing lost and nothing
 * duplicated, which is the failure mode worth having. Doing this atomically
 * needs an RPC; that is a migration, and it is not this screen's to write.
 */
export function planStepSwap(
  steps: readonly EnrollmentStep[],
  stepId: number,
  direction: "up" | "down",
): SwapPlan | { blocked: string } | null {
  const ordered = inOrder(steps);
  const index = ordered.findIndex((s) => s.id === stepId);
  if (index === -1) return null;

  const neighbor = direction === "up" ? index - 1 : index + 1;
  if (neighbor < 0 || neighbor >= ordered.length) return null;

  const first = ordered[Math.min(index, neighbor)];
  const second = ordered[Math.max(index, neighbor)];

  for (const step of [first, second]) {
    const verdict = retimeVerdict(step);
    if (!verdict.ok)
      return {
        blocked: `Step ${step.step_no} cannot change places. ${verdict.reason}`,
      };
  }

  const park = ordered.reduce((max, s) => Math.max(max, s.step_no), 0) + 1;

  const writes: StepWrite[] = [
    // 1. Park the first row's number so the second can take it.
    { id: first.id, data: { step_no: park } },
    // 2. The second row moves up into the vacated position, taking its
    //    schedule with it.
    {
      id: second.id,
      data: {
        step_no: first.step_no,
        due_at: first.due_at,
        offset_days: first.offset_days,
        ...EDITED,
      },
    },
    // 3. The parked row comes down into the position the second just left.
    {
      id: first.id,
      data: {
        step_no: second.step_no,
        due_at: second.due_at,
        offset_days: second.offset_days,
        ...EDITED,
      },
    },
  ];

  const after = ordered.map((step) => {
    if (step.id === first.id)
      return {
        ...step,
        step_no: second.step_no,
        due_at: second.due_at,
        offset_days: second.offset_days,
      };
    if (step.id === second.id)
      return {
        ...step,
        step_no: first.step_no,
        due_at: first.due_at,
        offset_days: first.offset_days,
      };
    return step;
  });

  return { first, second, writes, after: inOrder(after) };
}

/* ── adding a step from the content library ────────────────────────────── */

/** A content asset, reduced to what a plan needs from it. */
export type PlannableAsset = { id: number; name: string; kind: string };

/**
 * The `content_assets.kind` → `tasks.type` mapping, transcribed from
 * private.enrollment_step_task_type() (075).
 *
 * DELIBERATELY NOT EXHAUSTIVE over `content_assets_kind_check`. `linkedin`,
 * `note` and `other` are permitted content kinds with no clean counterpart in
 * `configuration.config->'taskTypes'`, and 066 exists because a
 * plausible-looking task type was once assumed rather than checked — 67 tasks
 * held the LABEL "Follow-up" instead of the value "follow-up" and rendered
 * correctly while being wrong.
 */
export const DERIVED_TASK_TYPE: Record<string, string> = {
  email: "email",
  call_script: "call",
};

export type TaskTypeChoice =
  | { kind: "derived"; taskType: string; note: string }
  | { kind: "must-choose"; note: string };

/**
 * What task type a step pointing at this asset will produce.
 *
 * THIS IS A GUARD AGAINST A WRITE THE DATABASE WILL REFUSE, not a preference.
 * `enrollment_steps_task_type_is_configured` fires BEFORE INSERT and derives
 * the effective type through the mapping above; a `linkedin` asset with no
 * override raises, and the person adding the step sees a constraint error
 * instead of a question. Asking the question first is the whole difference.
 *
 * `null` asset — a bare "call them, no script" step — is legitimate and
 * `content_id` is nullable, but it derives no type either, so it takes the
 * same must-choose branch.
 */
export function taskTypeFor(
  asset: PlannableAsset | null | undefined,
): TaskTypeChoice {
  if (!asset)
    return {
      kind: "must-choose",
      note: "A step with no content asset has nothing to derive a task type from, so say what this step is.",
    };

  const derived = DERIVED_TASK_TYPE[asset.kind];
  if (derived)
    return {
      kind: "derived",
      taskType: derived,
      note: `A ${asset.kind} asset produces a ${derived} task. Set a type only to override that.`,
    };

  return {
    kind: "must-choose",
    note: `No task type can be derived from a ${asset.kind} asset — the mapping covers email and call scripts only — so the database will refuse this step until you say what it is.`,
  };
}

/* ── the writes themselves ─────────────────────────────────────────────── */

/**
 * Why the writes live here and not in the component.
 *
 * Each of these is several requests that MUST happen in a particular order,
 * against a unique index with no transaction around them. That order is the
 * rule; a component that spread the calls through a click handler would put
 * the one thing worth asserting somewhere only a browser can reach it. With a
 * recording fake for `dataProvider` the order is provable in Node at ~1s --
 * see planRules.test.ts.
 *
 * NONE OF THEM INVALIDATES. Every one of these is called through
 * `useDataProvider()`, which does NOT go through react-admin's mutation hooks
 * and therefore invalidates nothing -- the failure mode the header of
 * invalidateTouched() describes, where the toast appears, the row is in the
 * database, and the screen keeps showing the old value. The caller invalidates
 * once, with invalidateLifecycle(), after the whole sequence of writes has
 * landed. Doing it per write would refetch the plan mid-renumber and paint a
 * state that exists for a few hundred milliseconds and is not the answer.
 */

/** Move one pending step to a chosen day, keeping its time. */
export async function retimeStep(
  dataProvider: DataProvider,
  step: EnrollmentStep,
  day: string,
): Promise<void> {
  const verdict = retimeVerdict(step);
  if (!verdict.ok) throw new Error(verdict.reason);
  const check = validateDueDay(day);
  if (!check.ok) throw new Error(check.message);

  await dataProvider.update("enrollment_steps", {
    id: step.id,
    previousData: step,
    data: { due_at: retimedDueAt(step.due_at, check.day), edited: true },
  });
}

/**
 * Set a pending step aside, with the reason the CHECK constraint requires.
 *
 * BOTH COLUMNS MOVE IN ONE UPDATE. `enrollment_steps_skip_has_reason` is
 * checked per row per statement, so writing the status first and the reason
 * second would be refused by the database halfway through -- which is the
 * correct outcome, and the reason not to write it that way.
 */
export async function skipStep(
  dataProvider: DataProvider,
  step: EnrollmentStep,
  reason: string,
): Promise<void> {
  const verdict = skipVerdict(step);
  if (!verdict.ok) throw new Error(verdict.reason);
  const check = validateSkipReason(reason);
  if (!check.ok) throw new Error(check.message);

  await dataProvider.update("enrollment_steps", {
    id: step.id,
    previousData: step,
    data: { status: "skipped", skipped_reason: check.reason, edited: true },
  });
}

/**
 * Remove one pending step and close the hole behind it.
 *
 * THE DELETE GOES FIRST, and that is not tidiness: it frees the number every
 * renumbered row is about to move down into. The trigger refuses the delete
 * outright if the step is not pending, so a refusal here costs one failed
 * request and rewrites nothing -- the order that puts the renumbering first
 * would leave a plan half-renumbered around a step that is still there.
 */
export async function removeStep(
  dataProvider: DataProvider,
  steps: readonly EnrollmentStep[],
  stepId: number,
): Promise<RemovalPlan> {
  const plan = planStepRemoval(steps, stepId);
  if (!plan)
    throw new Error(
      "That step is no longer in this plan — somebody else may have removed it. Reload before trying again.",
    );
  const verdict = removalVerdict(plan.remove);
  if (!verdict.ok) throw new Error(verdict.reason);

  await dataProvider.delete("enrollment_steps", {
    id: plan.remove.id,
    previousData: plan.remove,
  });

  for (const write of plan.writes)
    await dataProvider.update("enrollment_steps", {
      id: write.id,
      previousData: steps.find((s) => s.id === write.id),
      data: write.data,
    });

  return plan;
}

/**
 * Add one step, and move everything the addition displaces.
 *
 * THE CREATE IS LAST. Its `step_no` is only free once every later row has
 * moved up out of it, and those moves are written highest-first for the same
 * reason. An interruption before the create leaves a plan with a hole in its
 * numbering, which `enrollment_steps` tolerates -- 078 does no arithmetic on
 * `step_no` -- so nothing stops running; the plan is merely numbered oddly
 * until somebody adds the step again.
 */
export async function addStep(
  dataProvider: DataProvider,
  steps: readonly EnrollmentStep[],
  enrollmentId: number,
  spec: NewStep,
  now: Date = new Date(),
): Promise<InsertionPlan> {
  const plan = planStepInsertion(steps, enrollmentId, spec, now);
  if (!plan)
    throw new Error(
      "The step this one was to follow is no longer in the plan. Reload before trying again.",
    );

  for (const write of plan.writes)
    await dataProvider.update("enrollment_steps", {
      id: write.id,
      previousData: steps.find((s) => s.id === write.id),
      data: write.data,
    });

  await dataProvider.create("enrollment_steps", { data: plan.create });
  return plan;
}

/** Swap a step with its neighbor, schedule and all. */
export async function moveStep(
  dataProvider: DataProvider,
  steps: readonly EnrollmentStep[],
  stepId: number,
  direction: "up" | "down",
): Promise<void> {
  const plan = planStepSwap(steps, stepId, direction);
  if (!plan)
    throw new Error("There is nothing on that side to swap this step with.");
  if ("blocked" in plan) throw new Error(plan.blocked);

  for (const write of plan.writes)
    await dataProvider.update("enrollment_steps", {
      id: write.id,
      previousData: steps.find((s) => s.id === write.id),
      data: write.data,
    });
}
